//React Core Hooks
import { useEffect, useState, useContext } from "react";

// Router
import { useNavigate } from "react-router-dom";
import { Routes, Route } from "react-router-dom";
import { Navigate } from "react-router-dom";

// Contexts / Auth
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../context/AuthContext";
import { useMessenger } from "./MessengerContext";
import { ThemeContext } from "./theme";
import { LanguageContext } from "./LanguageContext";
import { useModal } from "../constant/ModalContext.jsx";
import { SidebarProvider } from "../../src/pages/SidebarContext";
import { useLogout } from "../hooks/useLogout";
import { useProfile } from "../hooks/useProfile";

// Assets
import s2Image from "../Images/photos/s4.png";
import s7Image from "../Images/photos/s7.png";

// UI Components
import MiniChatBar from "./MiniChatBar";
import SidebarWithTopbar from "./sidebar";
import ViewTypeSwitcher from "../components/ViewTypeSwitcher";

//Helpers
import {
  getEnglishInitials,
  getAvatarColor as getStandardAvatarColor,
} from "../components/UserAvatar";

/****************************************************
 * Pages
 ****************************************************/

// Dashboard
import Dashboard from "./dashboard_Final/index.js";
// Employees
import EmployeesTable from "./EmployeeTable_Final/EmployeeManagement";
// Reports
import Reports from "./Reports_Final/Reports.js";
import Analytics from "./ComingSoon_Final/ComingSoon.js";
// Settings
import SettingsPage from "./settings_Final/settings.js";
// Payroll
import Payroll from "./payroll.js";
// Calendar & Attendance
import Calendar from "./Calendar";
import Attendance from "./Attendance";
// Admin / HR Modules
import Mo3tiAdminDocumentRequests from "./Mo3tiAdminDocumentRequests";
import ExcelSalaryProcessorWithTheme from "./Taxcalculator_Final/index.jsx";
import Mo3tiAdvancementManagement from "./mo3tiAdvanceRequest.js";
import LeaveApprovalPage from "./adminLeavePage.js";
import EmployeeActionsPage from "./changesPage.js";
import MessengerPage from "./MessagesPage.jsx";
import EscalationGroupMapping from "./EscalationGroupMapping.js";
// Configuration
import CompanyConfiguration from "./CompanyConfiguration";
import AdminLevelConfiguration from "./AdminLevelConfiguration";
/****************************************************
 * Icons
 ****************************************************/
import {
  User,
  LogOut,
  Bell,
  Plug,
  MapPinOff,
  Network,
  Settings,
  BarChart3,
  CalendarCheck,
  Users,
  CalculatorIcon,
  Calendar as CalendarIcon,
  FileText,
  Home,
  CreditCard,
  Sun,
  Moon,
  Calculator,
  GitPullRequestIcon,
  BookCheckIcon,
  DollarSignIcon,
  Coffee,
  LockIcon,
  MessageSquare,
  GitPullRequest,
  Shield,
  GitBranch,
} from "lucide-react";

const allowedArea = {
  lat: process.env.REACT_APP_OFFICE_LATITUDE, // example Cairo
  lng: process.env.REACT_APP_OFFICE_LONGITUDE,
  radius: 10000, // in meters (200m around point)
};
// Helper: calculate distance between two lat/lng in meters (Haversine formula)
function getDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000; // Earth radius in m
  const toRad = (deg) => (deg * Math.PI) / 180;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // distance in meters
}

function ProfessionalDashboard() {
  // Theme Context
  const { theme, setTheme } = useContext(ThemeContext);
  // 🔹 Add location state
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [modulePermissions, setModulePermissions] = useState(null);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const { language } = useContext(LanguageContext);
  const { profile } = useProfile();
  // ✅ Avatar helper functions - using standardized English-only initials
  const getAvatarColor = (name) => {
    return getStandardAvatarColor(name);
  };

  // Uses shared getEnglishInitials - NEVER displays Arabic initials
  const getInitials = (name, englishName = null) => {
    return getEnglishInitials(name, englishName, "US");
  };

  const formatMessageTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  const {
    conversations,
    messages,
    onlineUsers,
    currentUser,
    employeeProfilePics,
  } = useMessenger();
  const isRTL = language === "ar";
  const translations = {
    en: {
      // Navigation
      dashboard: "Dashboard",
      employees: "Employees",
      payroll: "Payroll",
      analytics: "Analytics(Locked)",
      attendance: "Attendance",
      reports: "Reports",
      calendar: "Calendar",
      messages: "Messages",
      EscalationGroupMapping: "Escalation Mapping",
      documentsManagement: "Documents ",
      leaveManagement: "Leave ",
      solfaManagement: "Solfa ",
      employeeChanges: "Employee Changes",
      taxCalculator: "Tax Calculator",
      adminLevels: "Admin Levels",
      companyConfiguration: "Company Configuration",
      settings: "Settings",

      // Status Messages
      allowLocationAccess: "Please allow location access...",
      accessRestricted: "Access Restricted",
      outsideOfficeArea: "You are currently outside the permitted office area.",
      distanceFromOffice: "Distance from office",
      meters: "meters",
      moveCloserToOffice: "Move closer to the office to unlock",

      // Under Development
      underDevelopment:
        "This page is under development and will be available soon",
      pageName: "Page",
      effectiveManagement:
        "This page will contain all features and tools needed for effective",
      management: "management",

      // Loading
      loadingPermissions: "Loading permissions...",
      logout: "Logout",
      switchEmployee: "Employee View",
      accountActions: "Account Actions",
      accountActionsDescription: "Manage your account and session",
    },
    ar: {
      // Navigation
      dashboard: "لوحة التحكم",
      employees: "الموظفين",
      payroll: "الرواتب",
      analytics: "التحليلات (مقفلة)",
      attendance: "الحضور",
      reports: "التقارير",
      calendar: "التقويم",
      messages: "الرسائل",
      EscalationGroupMapping: "ربط الشكاوي",
      documentsManagement: "إدارة المستندات",
      leaveManagement: "إدارة الإجازات",
      solfaManagement: "إدارة السلف",
      employeeChanges: "تغييرات الموظفين",
      taxCalculator: "حاسبة الضرائب",
      adminLevels: "مستويات الإدارة",
      companyConfiguration: "إعدادات الشركة",
      settings: "الإعدادات",

      // Status Messages
      allowLocationAccess: "الرجاء السماح بالوصول للموقع...",
      accessRestricted: "الوصول محظور",
      outsideOfficeArea: "أنت حاليًا خارج منطقة المكتب المسموح بها.",
      distanceFromOffice: "المسافة من المكتب",
      meters: "متر",
      moveCloserToOffice: "اقترب من المكتب لفتح",

      // Under Development
      underDevelopment: "هذه الصفحة قيد التطوير وستكون متاحة قريبًا",
      pageName: "صفحة",
      effectiveManagement:
        "ستحتوي هذه الصفحة على جميع الميزات والأدوات اللازمة لـ",
      management: "إدارة فعالة",

      // Loading
      loadingPermissions: "جاري تحميل الصلاحيات...",

      logout: "تسجيل الخروج",
      switchEmployee: "عرض الموظف",
      accountActions: "إجراءات الحساب",
      accountActionsDescription: "إدارة حسابك والجلسة",
    },
  };

  const t = translations[language];
  // Fetch user location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLocation({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          });
        },
        (err) => {
          console.error("Error getting location:", err);
          setLocationError("Location access denied. Please enable it.");
        },
      );
    } else {
      setLocationError("Geolocation is not supported in this browser.");
    }
  }, []);

  // State for employee counts and payroll
  const [employeeCount, setEmployeeCount] = useState(0);
  const [totalCompanies, setTotalCompanies] = useState(15);
  const [counts, setCounts] = useState({ Active: 0, OnLeave: 0, Recent: 0 });
  const [payrollTotal, setPayrollTotal] = useState(0);

  const BASE_URL = process.env.REACT_APP_BASE_URL;

  // Fetch employee total count
  useEffect(() => {
    apiFetch(`${BASE_URL}/employees/count`, { method: "GET" })
      .then((res) => res.json())
      .then((data) => setEmployeeCount(data.count))
      .catch((err) => console.error(err));
  }, []);

  // Fetch counts for Active, OnLeave, and Recent employees
  useEffect(() => {
    async function fetchCounts() {
      try {
        const activeRes = await apiFetch(`${BASE_URL}/employees/Active`, {
          method: "GET",
        });
        const onLeaveRes = await apiFetch(`${BASE_URL}/employees/OnLeave`, {
          method: "GET",
        });
        const active = await activeRes.json();
        const onLeave = await onLeaveRes.json();
        setCounts({
          Active: active.count,
          OnLeave: onLeave.count,
          Recent: 7,
        });
      } catch (err) {
        console.error(err);
      }
    }
    fetchCounts();
  }, []);

  // Fetch total payroll
  useEffect(() => {
    apiFetch(`${BASE_URL}/payroll/total`, { method: "GET" })
      .then((res) => res.json())
      .then((data) => setPayrollTotal(data.total))
      .catch((err) => console.error(err));
  }, []);

  // User info from /users/me (profile), fall back to localStorage
  const user = {
    username: profile?.employeeName || localStorage.getItem("emoloyeeName"),
    email: profile?.employeeEmail || localStorage.getItem("email"),
    role: profile?.role || localStorage.getItem("role"),
  };

  const [notifications, setNotifications] = useState(3);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [currentPage, setCurrentPage] = useState(() => {
    // استرجاع الصفحة المحفوظة عند التحميل
    const savedPage = localStorage.getItem("adminCurrentPage");
    return savedPage || "dashboard";
  });
  const [unreadMessages, setUnreadMessages] = useState(0);

  const [viewType, setViewType] = useState(() => {
    return localStorage.getItem("viewType") || "admin";
  });
  const {
    conversations: messengerConversations,
    socket,
    isConnected,
  } = useMessenger();
  // const [language, setLanguage] = useState("en");

  const navigate = useNavigate();
  const { hasModuleAccess } = useAuth();
  const { logout } = useLogout();
  const { showModal } = useModal();

  // If MessengerContext already has conversations loaded (Messages page), use that as the primary source
  useEffect(() => {
    try {
      if (
        Array.isArray(messengerConversations) &&
        messengerConversations.length > 0
      ) {
        const totalFromContext = messengerConversations.reduce((s, c) => {
          const u =
            c.unread ?? c.unreadCount ?? c.unread_count ?? c.unreads ?? 0;
          return s + (Number(u) || 0);
        }, 0);
        console.log(
          "[DashboardManager] Using MessengerContext unread total:",
          totalFromContext,
        );
        setUnreadMessages(totalFromContext);
      }
    } catch (err) {
      console.warn(
        "[DashboardManager] Error computing unread from MessengerContext",
        err,
      );
    }
  }, [messengerConversations]);

  // Get admin level from profile (/users/me) or localStorage
  const hasAdminLevel = () => {
    if (profile?.adminLevel) {
      const al = profile.adminLevel;
      return Boolean(al.adminLevelId || (Array.isArray(al.modules) && al.modules.length > 0));
    }
    const adminLevel = localStorage.getItem("adminLevel");
    if (!adminLevel) return false;
    try {
      const parsed = JSON.parse(adminLevel);
      return (
        parsed?.adminLevelId || (parsed?.modules && parsed.modules.length > 0)
      );
    } catch (e) {
      return false;
    }
  };

  // Handle view type change with navigation
  const handleViewTypeChange = (type) => {
    setViewType(type);
    localStorage.setItem("viewType", type);
    if (type === "user") {
      // If user is admin (with or without adminLevel), go to userprofileadmin
      // Otherwise check if has admin level, go to userprofileadmin, else go to userprofilemanager
      if (user.role === "admin" || hasAdminLevel()) {
        navigate("/userprofileadmin/professionalProfile");
      } else {
        navigate("/userprofilemanager/professionalProfile");
      }
    } else if (type === "manager") {
      navigate("/DashboardManager/dashboard");
    }
    if (type === "admin") {
      navigate("/dashboard");
    }
  };

  // Toggle between light and dark theme
  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
  };
  useEffect(() => {
    // حفظ الصفحة الحالية في localStorage عند تغييرها
    localStorage.setItem("adminCurrentPage", currentPage);
  }, [currentPage]);
  useEffect(() => {
    const fetchModulePermissions = async () => {
      try {
        const clientName = profile?.clientName || localStorage.getItem("clientName") || "";
        if (!clientName) {
          setLoadingPermissions(false);
          return;
        }

        const response = await apiFetch(
          `${BASE_URL}/api/configurations/${encodeURIComponent(clientName)}`,
          { method: "GET" },
        );
        const json = await response.json();

        if (json) {
          setModulePermissions(json.data);
        } else {
          setModulePermissions({ privateMessaging: false });
        }
      } catch (error) {
        console.error("Error fetching module permissions:", error);
        setModulePermissions({ privateMessaging: false });
      } finally {
        setLoadingPermissions(false);
      }
    };

    fetchModulePermissions();
  }, []);

  // جلب عدد الرسائل غير المقروءة (مرة واحدة فقط عند التحميل)
  useEffect(() => {
    const fetchUnreadMessages = async () => {
      try {
        if (!BASE_URL) {
          console.log("[Dashboard] BASE_URL not set");
          return;
        }

        console.log(
          "[Dashboard] Fetching initial unread count from:",
          `${BASE_URL}/api/private-messages/conversations`,
        );

        const response = await apiFetch(
          `${BASE_URL}/api/private-messages/conversations`,
          { method: "GET" },
        );

        console.log(
          "[Dashboard] Response status:",
          response.status,
          response.ok,
        );

        if (response.ok) {
          const data = await response.json();
          console.log("[Dashboard] Raw API Response:", data);

          // البيانات قد تأتي كـ array مباشرة أو داخل object
          let conversations = [];
          if (Array.isArray(data)) {
            conversations = data;
          } else if (data.data && Array.isArray(data.data)) {
            conversations = data.data;
          } else if (data.conversations && Array.isArray(data.conversations)) {
            conversations = data.conversations;
          }

          console.log(
            "[Dashboard] Parsed conversations array length:",
            conversations.length,
          );

          // حساب إجمالي الرسائل غير المقروءة
          let total = 0;
          conversations.forEach((conv, index) => {
            // البحث عن unreadCount بطرق مختلفة
            const unreadCount =
              conv.unreadCount ??
              conv.unread_count ??
              conv.unreadMessagesCount ??
              conv.unreads ??
              0;

            total += Number(unreadCount) || 0;

            if (index < 3) {
              // لا نطبع جميعها، فقط أول 3
              console.log(
                `[Dashboard] Conversation: "${conv.recipientName || conv.name || "Unknown"}" - Unread: ${unreadCount}`,
              );
            }
          });

          console.log("[Dashboard] ✅ Initial unread messages count:", total);
          setUnreadMessages(total);
        } else {
          console.error(
            "[Dashboard] ❌ API returned error status:",
            response.status,
            await response.text(),
          );
        }
      } catch (error) {
        console.error("[Dashboard] ❌ Error fetching unread messages:", error);
      }
    };

    fetchUnreadMessages();
    // No polling - we'll use real-time socket events instead
  }, [BASE_URL]);

  // Real-time socket listener for new messages
  useEffect(() => {
    if (!socket || !isConnected) {
      console.log("[Dashboard] Socket not ready for unread messages listener");
      return;
    }

    console.log("[Dashboard] Setting up real-time message listeners");

    // Increment counter when new message is received
    const handleMessageReceived = (data) => {
      console.log("[Dashboard] 📩 New message received:", data);
      setUnreadMessages((prev) => {
        const newCount = prev + 1;
        console.log(`[Dashboard] Unread count: ${prev} → ${newCount}`);
        return newCount;
      });
    };

    // Decrement counter when messages are marked as read
    const handleMessagesRead = (data) => {
      console.log("[Dashboard] ✓ Messages marked as read:", data);
      if (data.count) {
        setUnreadMessages((prev) => {
          const newCount = Math.max(0, prev - data.count);
          console.log(`[Dashboard] Unread count: ${prev} → ${newCount}`);
          return newCount;
        });
      }
    };

    socket.on("private_message:received", handleMessageReceived);
    socket.on("private_message:read", handleMessagesRead);

    console.log("[Dashboard] ✅ Real-time message listeners active");

    return () => {
      socket.off("private_message:received", handleMessageReceived);
      socket.off("private_message:read", handleMessagesRead);
      console.log("[Dashboard] Cleaned up message listeners");
    };
  }, [socket, isConnected]);

  // Get background image based on theme
  // const getBackgroundImage = () => (theme === "light" ? s7Image : s2Image);
  const isPrivateMessagingEnabled =
    modulePermissions?.privateMessaging ?? false;
  // Logout function
  const handleLogout = async () => {
    try {
      localStorage.removeItem("adminCurrentPage");
      localStorage.removeItem("nav_admin");
      localStorage.removeItem("nav_manager");
      localStorage.removeItem("nav_employee");
    } catch (e) {}

    await logout(); // ← hook handles backend revocation + session clear + navigate
  };
  const handleSwitchEmployee = () => {
    try {
      // Clear the saved page when switching
      localStorage.removeItem("adminCurrentPage");
      // Reset to dashboard
      setCurrentPage("dashboard");
      navigate("/userprofileadmin/professionalProfile");
    } catch (error) {
      console.error("Switch error:", error);
      navigate("/dashboard");
    }
  };

  // Change current page
  const handlePageChange = (page) => {
    const navItem = navItems.find((item) => item.page === page);
    if (navItem?.locked) {
      const title = (t && t.notice) || "Notice";
      const message =
        navItem.lockMessage || (t && t.featureNotAvailable) || undefined;
      showModal({
        title,
        message,
        options: {
          requestAccessEndpoint: process.env.REACT_APP_BASE_URL
            ? `${process.env.REACT_APP_BASE_URL}/api/request-module-access`
            : null,
          module: navItem.page,
        },
      });
      return;
    }
    setCurrentPage(page);
    navigate(`/dashboard/${page}`);
  };

  // Mapping between page names and module names for permission checking
  const pageToModuleMap = {
    dashboard: null, // Dashboard is always visible
    employees: "employees",
    payroll: "payroll",
    attendance: "attendance",
    reports: "reports",
    calendar: "calendar",
    messages: "messages",
    EscalationGroupMapping: "Escalation Mapping",
    Mo3tiAdminDocumentRequests: "documents",
    LeaveApprovalPage: "leaves",
    Mo3tiAdvancementManagement: "solfa",
    ChangesPage: "changesPage", // Employee changes uses separate changesPage module
    TaxCalculator: "payroll", // Tax calculator is part of payroll
    adminLevels: "adminLevels",
    configuration: "configurations",
    Settings: "configurations",
  };

  // Navigation menu items (unfiltered)
  const allNavItems = [
    { icon: Home, text: t.dashboard, page: "dashboard" },
    { icon: Users, text: t.employees, page: "employees" },
    { icon: CreditCard, text: t.payroll, page: "payroll" },
    // { icon: BarChart3, text: t.analytics, page: "analytics" },
    { icon: CalendarCheck, text: t.attendance, page: "attendance" },
    { icon: FileText, text: t.reports, page: "reports" },
    { icon: CalendarIcon, text: t.calendar, page: "calendar" },
    {
      icon: MessageSquare,
      text: isPrivateMessagingEnabled ? t.messages : `${t.messages} `,
      page: "messages",
      locked: !isPrivateMessagingEnabled,
      badge: unreadMessages,
      badgeColor: "#ef4444",
    },
    {
      icon: GitBranch,
      text: t.EscalationGroupMapping,
      page: "EscalationGroupMapping",
    },
    {
      icon: BookCheckIcon,
      text: t.documentsManagement,
      page: "Mo3tiAdminDocumentRequests",
    },
    { icon: Coffee, text: t.leaveManagement, page: "LeaveApprovalPage" },
    {
      icon: DollarSignIcon,
      text: t.solfaManagement,
      page: "Mo3tiAdvancementManagement",
    },
    { icon: GitPullRequestIcon, text: t.employeeChanges, page: "ChangesPage" },
    { icon: CalculatorIcon, text: t.taxCalculator, page: "TaxCalculator" },
    { icon: Shield, text: t.adminLevels, page: "adminLevels" },
    { icon: LockIcon, text: t.companyConfiguration, page: "configuration" },
    // ✅ المشكلة كانت هنا - Settings كانت بدون ترجمة:
    { icon: Settings, text: t.settings, page: "Settings" }, // ✅ كانت: text: "Settings"
  ];

  // Filter navigation items based on user's admin level permissions
  const navItems = allNavItems.filter((item) => {
    // Dashboard is always visible
    if (item.page === "dashboard") return true;

    // Super admins see everything
    if (user.role === "superadmin") return true;

    // Regular admins see everything except pages that are explicitly locked
    if (user.role === "admin") {
      // Allow admins to see all pages including configuration
      return true;
    }

    // Get the required module for this page
    const requiredModule = pageToModuleMap[item.page];

    // If no module mapping exists, show the item (backward compatibility)
    if (!requiredModule) {
      console.log(
        `[Dashboard] No module mapping for page: ${item.page} - showing by default`,
      );
      return true;
    }

    // Check if user has access to the required module
    const hasAccess = hasModuleAccess(requiredModule);

    // Log configuration page access for debugging
    if (item.page === "configuration") {
      console.log(`[Dashboard] Configuration page access check:`, {
        page: item.page,
        requiredModule,
        hasAccess,
        userRole: user.role,
        hasAdminLevel: hasAdminLevel(),
      });
    }

    return hasAccess;
  });

  // Renderers for all pages
  const renderDashboard = () => <Dashboard />;
  const renderEmployees = () => <EmployeesTable />;
  const renderPayroll = () => <Payroll />;
  const renderAttendance = () => <Attendance />;
  const renderAnalytics = () => <Analytics />;
  const renderReports = () => <Reports />;
  const renderMessages = () => <MessengerPage />;
  const renderEscalationGroupMapping = () => <EscalationGroupMapping />;
  const renderCalendar = () => <Calendar />;
  //const renderConnector = () => <Connectors />;
  const renderSettings = () => <SettingsPage />;
  const renderMo3tiAdminDocumentRequests = () => <Mo3tiAdminDocumentRequests />;
  const renderMo3tiAdvancementManagement = () => <Mo3tiAdvancementManagement />;
  const renderAdminLeavePage = () => <LeaveApprovalPage />;
  const renderChangesPage = () => <EmployeeActionsPage />;
  const renderConfiguration = () => <CompanyConfiguration />;
  const renderAdminLevels = () => <AdminLevelConfiguration />;

  const renderTaxCalculator = () => <ExcelSalaryProcessorWithTheme />;

  // Placeholder renderer for under-development pages
  const renderOtherPage = (pageName) => (
    <div>
      <div style={{ marginBottom: "32px" }}>
        <h1
          style={{
            color: theme === "light" ? "#0f172a" : "white",
            fontSize: "28px",
            fontWeight: "700",
          }}
        >
          {t[pageName] || pageName}
        </h1>
        <p
          style={{
            color: theme === "light" ? "#64748b" : "rgba(255,255,255,0.8)",
          }}
        >
          {t.underDevelopment}
        </p>
      </div>
      <div
        style={{
          padding: "24px",
          background: theme === "light" ? "#fff" : "#1e293b",
          borderRadius: "16px",
          textAlign: "center",
        }}
      >
        <Settings size={64} />
        <h3 style={{ color: theme === "light" ? "#0f172a" : "white" }}>
          {t[pageName] || pageName} {t.pageName}
        </h3>
        <p
          style={{
            color: theme === "light" ? "#64748b" : "rgba(255,255,255,0.6)",
          }}
        >
          {t.effectiveManagement}
          <br />
          {pageName.toLowerCase()} {t.management}
        </p>
      </div>
    </div>
  );

  // Render current page based on navigation
  const renderCurrentPage = () => {
    switch (currentPage) {
      case "dashboard":
        return renderDashboard();
      case "employees":
        return renderEmployees();
      case "payroll":
        return renderPayroll();
      case "analytics":
        return renderAnalytics();
      case "attendance":
        return renderAttendance();
      case "reports":
        return renderReports();
      // case "messages": // ⬅️ أضف هذا
      //   return renderMessages();
      case "calendar":
        return renderCalendar();
      case "configuration":
        return renderConfiguration();
      // case "connectors": {
      //   // If no location yet
      //   if (!userLocation && !locationError) {
      //     return <div>📍 Please allow location access...</div>;
      //   }

      //   if (locationError) {
      //     return (
      //       <div style={{ color: "red", padding: "20px" }}>{locationError}</div>
      //     );
      //   }

      //   // Calculate distance
      //   const distance = getDistance(
      //     userLocation.lat,
      //     userLocation.lng,
      //     allowedArea.lat,
      //     allowedArea.lng
      //   );

      //   if (distance > allowedArea.radius) {
      //     return (
      //       <div className="fixed inset-0 flex items-center justify-center z-50">
      //         {/* Semi-transparent backdrop */}
      //         <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" />

      //         {/* Modal container */}
      //         <div className="relative bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg rounded-3xl shadow-2xl p-8 flex flex-col items-center max-w-xs text-center border border-red-400/20">
      //           {/* Glowing semi-transparent icon */}
      //           <div className="w-24 h-24 flex items-center justify-center mb-5 relative">
      //             <MapPinOff
      //               size={60}
      //               className="text-red-500/20 dark:text-red-400/20"
      //             />
      //             <div className="absolute inset-0 rounded-full bg-red-500/10 blur-xl animate-pulse" />
      //           </div>

      //           {/* Title */}
      //           <h2 className="text-2xl font-extrabold text-red-600 dark:text-red-400 mb-2">
      //             Access Restricted
      //           </h2>

      //           {/* Subtitle */}
      //           <p className="text-gray-700 dark:text-gray-300 mb-4">
      //             You are currently{" "}
      //             <strong className="font-semibold text-red-500 dark:text-red-300">
      //               outside
      //             </strong>{" "}
      //             the permitted office area.
      //           </p>

      //           {/* Distance card */}
      //           <div className="w-full bg-red-50/30 dark:bg-slate-800/30 rounded-xl p-4 mb-4 shadow-inner">
      //             <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
      //               Distance from office
      //             </p>
      //             <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
      //               {Math.round(distance).toLocaleString()}{" "}
      //               <span className="text-sm font-medium">meters</span>
      //             </p>
      //           </div>

      //           {/* Call to action */}
      //           <p className="text-sm text-gray-600 dark:text-gray-400">
      //             Move closer to the{" "}
      //             <strong className="font-semibold text-red-500 dark:text-red-300">
      //               office
      //             </strong>{" "}
      //             to unlock{" "}
      //             <strong className="font-semibold text-gray-900 dark:text-gray-100">
      //               Connectors
      //             </strong>
      //             .
      //           </p>
      //         </div>
      //       </div>
      //     );
      //   }
      //   // ✅ Allowed → show connectors page
      //   return renderConnector();
      // }
      case "messages": {
        // If no location yet
        if (!userLocation && !locationError) {
          return <div>📍 Please allow location access...</div>;
        }

        if (locationError) {
          return (
            <div style={{ color: "red", padding: "20px" }}>{locationError}</div>
          );
        }

        // Calculate distance
        const distance = getDistance(
          userLocation.lat,
          userLocation.lng,
          allowedArea.lat,
          allowedArea.lng,
        );

        if (distance > allowedArea.radius) {
          return (
            <div className="fixed inset-0 flex items-center justify-center z-50">
              <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" />

              <div className="relative bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg rounded-3xl shadow-2xl p-8 flex flex-col items-center max-w-xs text-center border border-red-400/20">
                <div className="w-24 h-24 flex items-center justify-center mb-5 relative">
                  <MapPinOff
                    size={60}
                    className="text-red-500/20 dark:text-red-400/20"
                  />
                  <div className="absolute inset-0 rounded-full bg-red-500/10 blur-xl animate-pulse" />
                </div>

                <h2 className="text-2xl font-extrabold text-red-600 dark:text-red-400 mb-2">
                  {t.accessRestricted}
                </h2>

                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  {t.outsideOfficeArea}
                </p>

                <div className="w-full bg-red-50/30 dark:bg-slate-800/30 rounded-xl p-4 mb-4 shadow-inner">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    {t.distanceFromOffice}
                  </p>
                  <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    {Math.round(distance).toLocaleString()}{" "}
                    <span className="text-sm font-medium">{t.meters}</span>
                  </p>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t.moveCloserToOffice}{" "}
                  <strong className="font-semibold text-gray-900 dark:text-gray-100">
                    {t.messages}
                  </strong>
                  .
                </p>
              </div>
            </div>
          );
        }
        // ✅ Allowed → show connectors page
        return renderMessages();
      }
      case "Settings":
        return renderSettings();
      case "Mo3tiAdminDocumentRequests":
        return renderMo3tiAdminDocumentRequests();
      case "Mo3tiAdvancementManagement":
        return renderMo3tiAdvancementManagement();
      case "TaxCalculator":
        return renderTaxCalculator();
      case "LeaveApprovalPage":
        return renderAdminLeavePage();
      case "ChangesPage":
        return renderChangesPage();
      case "adminLevels":
        return renderAdminLevels();

      default:
        return renderDashboard();
    }
  };
  if (loadingPermissions) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div
          style={{
            color: theme === "dark" ? "white" : "#0f172a",
            fontSize: "18px",
          }}
        >
          {t.loadingPermissions}
        </div>
      </div>
    );
  }
  // Main render
  return (
    <div
      style={{
        minHeight: "100vh",
        fontFamily: "Segoe UI, Roboto, sans-serif",
        position: "relative",
      }}
    >
      {/* Background image */}

      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        <div
          style={{
            position: "absolute",
            top: "-140px",
            right: "-120px",
            width: "300px",
            height: "300px",
            borderRadius: "50%",
            filter: "blur(60px)",
            background:
              theme === "light"
                ? "rgba(59,130,246,0.05)"
                : "rgba(99,102,241,0.1)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-140px",
            left: "-140px",
            width: "320px",
            height: "320px",
            borderRadius: "50%",
            filter: "blur(60px)",
            background:
              theme === "light"
                ? "rgba(139,92,246,0.05)"
                : "rgba(59,130,246,0.1)",
          }}
        />
      </div>

      {/* Sidebar with Topbar */}
      <SidebarWithTopbar
        navItems={navItems}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        user={user}
        notifications={notifications}
        handleLogout={handleLogout}
        handleSwitchEmployee={handleSwitchEmployee}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        themeToggle={
          <button
            style={{
              padding: "8px",
              borderRadius: "12px",
              cursor: "pointer",
              color: theme === "light" ? "#0f172a" : "white",
              background:
                theme === "light" ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)",
            }}
            onClick={toggleTheme}
          >
            {theme === "light" ? <Moon size={20} /> : <Sun size={20} />}
          </button>
        }
        viewModeSwitcher={
          <ViewTypeSwitcher
            viewType={viewType}
            onSelect={(type) => handleViewTypeChange(type)}
            hasAdminLevel={() => hasAdminLevel()}
            role={user.role}
          />
        }
      >
        {/* ❌ BEFORE - شيل السطر ده */}
        {/* <div style={{ padding: "32px 24px" }}>{renderCurrentPage()}</div> */}

        {/* ✅ AFTER - استبدله بده */}
        <div
          style={{
            padding: "0",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            //overflow: "hidden", // ✅ أضف هذا
            marginTop: 40,
          }}
        >
          <Routes>
            <Route path="/dashboard" element={renderDashboard()} />
            <Route path="/employees" element={renderEmployees()} />
            <Route path="/payroll" element={renderPayroll()} />
            <Route path="/attendance" element={renderAttendance()} />
            <Route path="/analytics" element={renderAnalytics()} />
            <Route path="/reports" element={renderReports()} />
            <Route path="/calendar" element={renderCalendar()} />
            {/* <Route path="/messages" element={renderMessages()} /> */}
            <Route path="/configuration" element={renderConfiguration()} />
            <Route path="/adminLevels" element={renderAdminLevels()} />

            {/* <Route
  path="/connectors"
  element={
    isPrivateMessagingEnabled ? (
      <Connectors />
    ) : (
      <Navigate to="/dashboard" replace />
    )
  }
/> */}
            <Route
              path="/messages"
              element={
                isPrivateMessagingEnabled ? (
                  <MessengerPage /> // ✅ صح: حرف كبير
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route
              path="/EscalationGroupMapping"
              element={renderEscalationGroupMapping()}
            />
            <Route
              path="/Mo3tiAdminDocumentRequests"
              element={renderMo3tiAdminDocumentRequests()}
            />
            <Route
              path="/Mo3tiAdvancementManagement"
              element={renderMo3tiAdvancementManagement()}
            />
            <Route path="/TaxCalculator" element={renderTaxCalculator()} />
            <Route path="/LeaveApprovalPage" element={renderAdminLeavePage()} />
            <Route path="/ChangesPage" element={renderChangesPage()} />
            <Route path="/Settings" element={renderSettings()} />
          </Routes>
        </div>
        {/* </SidebarWithTopbar>

    </div>
  );
}

export default ProfessionalDashboard; */}
      </SidebarWithTopbar>

      {/* ❄️ Snow Animation - تأثير الثلج */}
      {/* <div className="snowfall">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="snowflake"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${5 + Math.random() * 10}s`,
              opacity: Math.random() * 0.6 + 0.4,
              fontSize: `${12 + Math.random() * 12}px`
            }}
          >
            {['❄', '❅', '❆', '✻', '✼', '❉'][Math.floor(Math.random() * 6)]}
          </div>
        ))}
      </div> */}
      {/* ❄️ Snow Animation - تأثير الثلج */}
      {/* ... كود الثلج الموجود ... */}

      {/* ✅ أضف هنا Mini Chat Bar */}
      {/* ✅ Mini Chat Bar - في آخر الـ return قبل إغلاق الـ div */}
      {/* ✅ Mini Chat Bar - قبل إغلاق آخر </div> */}
      {/* <MiniChatBar
  conversations={conversations || []}
  onlineUsers={onlineUsers || new Set()}
  messages={messages || {}}
  currentUser={currentUser}
  socket={socket}
  isConnected={isConnected}
  onSendMessage={(chatId, message) => {
    if (!socket || !isConnected) return;
    
    socket.emit('private_message:send', {
      receiverId: chatId,
      content: message,
      type: 'text',
    });
  }}
  onOpenFullChat={() => handlePageChange('messages')}
  employeeProfilePics={employeeProfilePics || {}}
  getAvatarColor={getAvatarColor}
  getInitials={getInitials}
  formatMessageTime={formatMessageTime}
  renderMessageContent={(message) => message?.text || ''}
/> */}

      {/* Styles */}
      <style>
        {`
          /* Snow Animation Styles - أنماط تأثير الثلج */
          .snowfall {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 9999;
            overflow: hidden;
          }

          .snowflake {
            position: absolute;
            top: -20px;
            color: #fff;
            text-shadow: 
              0 0 10px rgba(255, 255, 255, 1),
              0 0 20px rgba(200, 230, 255, 0.8),
              0 0 30px rgba(150, 200, 255, 0.6);
            animation: fall linear infinite;
            user-select: none;
            filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.9));
          }

          @keyframes fall {
            0% {
              transform: translateY(0) translateX(0) rotate(0deg);
              opacity: 1;
            }
            25% {
              transform: translateY(25vh) translateX(20px) rotate(90deg);
              opacity: 0.8;
            }
            50% {
              transform: translateY(50vh) translateX(-20px) rotate(180deg);
              opacity: 0.6;
            }
            75% {
              transform: translateY(75vh) translateX(20px) rotate(270deg);
              opacity: 0.8;
            }
            100% {
              transform: translateY(100vh) translateX(0) rotate(360deg);
              opacity: 0;
            }
          }

          /* Responsive للموبايل */
          @media (max-width: 768px) {
            .snowflake {
              font-size: 12px !important;
            }
          }

          @media (max-width: 480px) {
            .snowflake {
              font-size: 10px !important;
            }
          }
        `}
      </style>
    </div> // <-- هنا آخر div قبل إغلاق الـ return
  );
}

export default ProfessionalDashboard;
